if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          container: ".m-main-product--wrapper",
          slider: ".m-media-gallery__inner",
          sliderPagination: ".swiper-pagination",
          sliderPrevEl: ".swiper-button-prev",
          sliderNextEl: ".swiper-button-next",
          navSlider: ".nav-swiper-container",
          medias: [".m-product-media--item"],
          mediaWrapper: ".m-product-media--wrapper",
          mediaZoomIns: [".m-product-media__zoom-in"],
          videos: [".media-video"],
          featuredImage: ".m-product-media",
          galleryList: ".m-media-gallery__list",
          galleryInner: ".m-media-gallery__inner",
        };

        this.navSlider = false;
        this.slider = false;
        this.allMedias = null;
        this.allThumbs = null;
        this._productVariants = null;
        this._initialized = false;
      }

      get context() {
        return this.dataset.context;
      }

      get layout() {
        return this.dataset.layout;
      }

      get hasVariants() {
        return this.dataset.hasVariants === "true";
      }

      connectedCallback() {
        this.init();

        this.variantChangedHandler = (event) => {
          const variant = event?.detail?.variant;
          const sectionId = event?.detail?.sectionId;
          if (variant && (!sectionId || sectionId === this.dataset.section)) {
            this.setActiveMedia(variant);
          }
        };
        document.addEventListener('variant:changed', this.variantChangedHandler);
      }

      disconnectedCallback() {
        if (this.variantChangedHandler) {
          document.removeEventListener('variant:changed', this.variantChangedHandler);
        }
      }

      init() {
        this.section = this.closest('product-info');
        this.domNodes = queryDomNodes(this.selectors, this);
        this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.enableVariantGroupImages = this.dataset.enableVariantGroupImages === "true";
        this.onlyMedia = this.dataset.onlyMedia === "true";
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.variantPicker = this.section ? this.section.querySelector("variant-picker") : null;

        // Cache original DOM elements created from initial render
        if (!this.allMedias || this.allMedias.length === 0) {
          const listItems = Array.from(this.querySelectorAll('.m-media-gallery__list > .m-product-media--item'));
          if (listItems.length > 0) {
            this.allMedias = listItems;
          }
        }
        if (!this.allThumbs || this.allThumbs.length === 0) {
          const thumbItems = Array.from(this.querySelectorAll('.m-product-media--slider__thumbnails .swiper-wrapper > .swiper-slide'));
          if (thumbItems.length > 0) {
            this.allThumbs = thumbItems;
          }
        }

        if (!this._initialized) {
          this._initialized = true;

          // Filter DOM elements for initial variant before initializing Swiper
          const initialVariant = this.getCurrentSelectedVariant();
          if (initialVariant) {
            this.filterMediaByAlt(initialVariant, true);
          }

          this.setSliderOptions();

          const mql = window.matchMedia(MinimogTheme.config.mediaQueryMobile);
          mql.onchange = this.updateMediaLayout.bind(this);
          this.updateMediaLayout();

          if (this.enableImageZoom) {
            this.handlePhotoswipe();
          }

          this.handleAutoplayVideo();

          this.removeAttribute("data-media-loading");
          if (this.domNodes.galleryInner) {
            this.domNodes.galleryInner.style.opacity = 1;
          }
        }
      }

      updateMediaLayout() {
        if (MinimogTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      setSliderOptions() {
        const { sliderPagination, sliderNextEl: nextEl, sliderPrevEl: prevEl } = this.domNodes;

        this.sliderOptions = {
          autoHeight: true,
          navigation: { nextEl, prevEl },
          threshold: 2,
          loop: false,
          pagination: {
            el: sliderPagination,
            clickable: true,
            type: "bullets",
          },
          on: {
            init: () => {
              this.domNodes.slider.style.opacity = 1;
              this.domNodes = queryDomNodes(this.selectors, this.section);
              requestAnimationFrame(() => {
                window.dispatchEvent(new Event("resize"));
              });
            }
          }
        };

        this.thumbsOptions = {
          loop: false,
          slidesPerView: 5,
          freeMode: true,
          spaceBetween: 10,
          threshold: 2,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          on: {
            init: () => {
              if (this.domNodes.navSlider) {
                this.domNodes.navSlider.style.opacity = 1;
              }
            }
          }
        };

        switch (this.layout) {
          case 'layout-4':
            break;
          case 'layout-5':
            this.sliderOptions = {
              ...this.sliderOptions,
              watchSlidesProgress: true,
              watchSlidesVisibility: true,
              breakpoints: {
                768: {
                  slidesPerView: 2,
                  slidesPerGroup: 1,
                  spaceBetween: 10,
                },
              }
            }
            break;
          case 'layout-6':
            this.thumbsOptions = {
              ...this.thumbsOptions,
              breakpoints: {
                1024: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                  spaceBetween: 5,
                }
              }
            }
            break;
          case 'layout-7':
            this.sliderOptions = {
              ...this.sliderOptions,
              speed: 500,
              centeredSlides: true,
              breakpoints: {
                768: {
                  slidesPerView: 3
                }
              }
            }
        }
      }

      initSlider() {
        if (typeof this.slider !== 'object') {
          const { slider, navSlider, galleryList } = this.domNodes;

          // Initialize navigation slider if navSlider exists
          this.navSlider = navSlider ? new MinimogLibs.Swiper(navSlider, this.thumbsOptions) : false;

          if (navSlider) {
            this.sliderOptions.thumbs = {
              swiper: this.navSlider
            };
          }

          // Initialize the main slider
          slider.classList.add('swiper-container');
          galleryList.classList.remove('m:grid');
          galleryList.classList.add('swiper-wrapper');

          this.slider = new MinimogLibs.Swiper(slider, this.sliderOptions);

          // Handle slide change
          this.handleSlideChange();
        }
      }

      destroySlider() {
        const { slider, galleryList } = this.domNodes;

        slider.classList.remove('swiper-container');
        galleryList.classList.add('m:grid');
        galleryList.classList.remove('swiper-wrapper');

        if (typeof this.slider === 'object') {
          this.slider.destroy();
          this.slider = false;
        }
      }

      handleSlideChange() {
        if (!this.slider) return;

        this.slider.on("slideChange", (swiper) => {
          if (typeof window.pauseAllMedia === "function") {
            window.pauseAllMedia(this);
          }

          const { slides, activeIndex } = swiper;
          const activeSlide = slides && slides[activeIndex];
          if (activeSlide) this.playActiveMedia(activeSlide);

          // Determine visible slides based on layout
          const visibleSlides = [activeIndex];
          if (["layout-5", "layout-7"].includes(this.layout)) {
            visibleSlides.push(activeIndex + 1);
          }

          // Check if any of the visible slides have a model media type
          const hasModelMediaType = visibleSlides.some((index) => {
            const slide = slides[index];
            return slide && slide.dataset.mediaType === "model";
          });

          // Toggle slider's draggable state based on media type
          this.toggleSliderDraggableState(!hasModelMediaType);
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.slider && this.slider.allowTouchMove !== isDraggable) {
          this.slider.allowTouchMove = isDraggable;
        }
      }

      handleAutoplayVideo() {
        const playVideo = (mediaElement) => {
          if (!mediaElement) return;
          const mediaType = mediaElement.dataset.mediaType;
          if (mediaType === "video" || mediaType === "external_video") {
            this.playActiveMedia(mediaElement);
          }
        };

        if (this.slider && this.slider.slides) {
          const { slides, activeIndex } = this.slider;
          const activeSlide = slides && slides[activeIndex];
          if (activeSlide) playVideo(activeSlide);
        } else {
          const currentMedias = Array.from(this.querySelectorAll('.m-media-gallery__list > .m-product-media--item'));
          currentMedias.forEach(playVideo);
        }
      }

      playActiveMedia(selected) {
        if (!selected) return;
        const deferredMedia = selected.querySelector("deferred-media");
        if (!deferredMedia) return;

        const playMedia = (element) => {
          if (!element) return;
          if (element.classList.contains("js-youtube") || element.classList.contains("js-vimeo")) {
            const platform = element.classList.contains("js-youtube") ? "youtube" : "vimeo";
            const param = platform === "youtube" ? "mute=1" : "muted=1";
            const symbol = element.src.includes("?") ? "&" : "?";
            if (!element.src.includes("autoplay=1")) {
              element.src += `${symbol}autoplay=1&${param}`;
            }
          } else if (element.tagName === "VIDEO" || typeof element.play === "function") {
            element.muted = true;
            element.playsInline = true;
            element.setAttribute("playsinline", "");
            element.setAttribute("webkit-playsinline", "");
            element.setAttribute("controlslist", "nodownload nofullscreen noremoteplayback");
            element.setAttribute("disablepictureinpicture", "true");
            element.play().catch(() => {});
          }
        };

        if (!deferredMedia.hasAttribute("loaded")) {
          deferredMedia.loadContent(false);
        }

        const deferredElement = deferredMedia.querySelector("video, model-viewer, iframe");
        if (deferredElement) {
          playMedia(deferredElement);
        }
      }

      handlePhotoswipe() {
        if (this.lightbox) {
          try {
            this.lightbox.destroy();
          } catch(e) {}
        }

        const medias = [...this.querySelectorAll(".m-media-gallery__list > .m-product-media--item")];

        const data = medias.map((media) => {
          const { mediaType, index: id } = media.dataset;

          if (mediaType === "image") {
            const mediaInner = media.querySelector(".m-product-media");
            const {
              mediaSrc: src,
              mediaWidth,
              mediaHeight,
              mediaAlt: alt,
            } = mediaInner ? mediaInner.dataset : {};
            return {
              src: src || '',
              width: parseInt(mediaWidth) || 1200,
              height: parseInt(mediaHeight) || 1200,
              alt: alt || '',
              id,
            };
          } else {
            return {
              html: `<div class="pswp__${mediaType}">${media.innerHTML}</div>`,
              type: mediaType,
              id,
            };
          }
        });

        const options = {
          dataSource: data,
          bgOpacity: 1,
          close: false,
          zoom: false,
          arrowNext: false,
          arrowPrev: false,
          counter: false,
          preloader: false,
          pswpModule: MinimogLibs.PhotoSwipeLightbox.PhotoSwipe,
        };
        this.lightbox = new MinimogLibs.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, { id }, index) => {
          return this.querySelector(`[data-index="${id}"] img`) || thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, { data: { id } }) => {
          const el = this.querySelector(`[data-index="${id}"] img`);
          return el ? el.src : placeholderSrc;
        });

        this.lightbox.on("change", () => {
          window.pauseAllMedia(this);
          if (this.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (typeof this.slider.slideTo === 'function') {
              this.slider.slideTo(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          if (this.lightbox.pswp.currSlide.data.type === "model") {
            e.preventDefault();
          }
        });

        const uiElements = [
          {
            name: "m-close",
            order: 11,
            isButton: true,
            html: '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',
            onClick: (event, el, pswp) => pswp.close(),
          },
          {
            name: "m-arrowNext",
            order: 12,
            isButton: true,
            html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>',
            onClick: (event, el, pswp) => pswp.next(),
          },
          {
            name: "m-arrowPrev",
            order: 10,
            isButton: true,
            html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',
            onClick: (event, el, pswp) => pswp.prev(),
          },
        ];

        this.lightbox.on("uiRegister", () => {
          this.lightbox.pswp.ui.registerElement(uiElements[0]);
          if (!this.onlyMedia) {
            uiElements.slice(1).forEach((element) => this.lightbox.pswp.ui.registerElement(element));
          }
        });

        this.lightbox.init();

        if (!this._photoSwipeEventBound) {
          this._photoSwipeEventBound = true;
          const handleMediaClick = (e, media) => {
            e.preventDefault();
            const isImage = media.classList.contains("media-type-image");
            const isZoomButton = e.target.closest(this.selectors.mediaZoomIns[0]);

            if (isImage || isZoomButton) {
              const index = Number(media.dataset.index) || 0;
              this.lightbox.loadAndOpen(index);
            }
          };

          addEventDelegate({
            selector: this.selectors.medias[0],
            context: this,
            handler: handleMediaClick,
          });
        }
      }

      getProductVariants() {
        if (this._productVariants) return this._productVariants;
        const scriptEl = this.querySelector('script[data-product-variants]');
        if (scriptEl) {
          try {
            this._productVariants = JSON.parse(scriptEl.textContent);
            return this._productVariants;
          } catch (e) {
            console.error('Error parsing product variants JSON', e);
          }
        }
        return [];
      }

      getCurrentSelectedVariant() {
        const pickerScript = this.section ? this.section.querySelector('variant-picker [data-selected-variant]') : null;
        if (pickerScript) {
          try {
            return JSON.parse(pickerScript.textContent);
          } catch (e) {}
        }
        const variants = this.getProductVariants();
        if (variants && variants.length > 0) {
          return variants[0];
        }
        return null;
      }

      filterMediaByAlt(variant, isInitial = false) {
        if (!variant) return false;

        const variantsData = this.getProductVariants();
        if (!variantsData || variantsData.length <= 1) {
          return false;
        }

        const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const clean = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const noSpace = (s) => (s || '').replace(/\s+/g, '').toLowerCase();

        const matchesTerm = (alt, term) => {
          if (!alt || !term) return false;
          const a = clean(alt);
          const t = clean(term);
          if (a === t || noSpace(a) === noSpace(t)) return true;

          const parts = a.split(/[,|/+#;_]/).map(p => p.trim()).filter(Boolean);
          if (parts.some(p => clean(p) === t || noSpace(p) === noSpace(t))) return true;

          try {
            const regex = new RegExp('(?:^|[^a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF])' + escapeRegex(t) + '(?:$|[^a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF])', 'i');
            if (regex.test(a)) return true;

            const regexNoSpace = new RegExp('(?:^|[^a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF])' + escapeRegex(noSpace(t)) + '(?:$|[^a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF])', 'i');
            if (regexNoSpace.test(noSpace(a))) return true;
          } catch (e) {
            if (a.includes(t) || noSpace(a).includes(noSpace(t))) return true;
          }

          return false;
        };

        const selectedTerms = [
          variant.title,
          variant.option1,
          variant.option2,
          variant.option3,
          ...(variant.options || [])
        ].filter(Boolean).map(t => String(t).trim().toLowerCase());

        const otherTermsSet = new Set();
        variantsData.forEach(other => {
          if (String(other.id) !== String(variant.id)) {
            [other.title, other.option1, other.option2, other.option3, ...(other.options || [])]
              .filter(Boolean)
              .forEach(t => {
                const val = String(t).trim().toLowerCase();
                if (!selectedTerms.includes(val)) {
                  otherTermsSet.add(val);
                }
              });
          }
        });
        const otherTerms = Array.from(otherTermsSet);

        if (!this.allMedias || this.allMedias.length === 0) {
          const listItems = Array.from(this.querySelectorAll('.m-media-gallery__list > .m-product-media--item'));
          if (listItems.length > 0) this.allMedias = listItems;
        }
        if (!this.allThumbs || this.allThumbs.length === 0) {
          const thumbItems = Array.from(this.querySelectorAll('.m-product-media--slider__thumbnails .swiper-wrapper > .swiper-slide'));
          if (thumbItems.length > 0) this.allThumbs = thumbItems;
        }

        const sourceMedias = this.allMedias || [];
        const sourceThumbs = this.allThumbs || [];

        // Check if ANY media has alt text matching any variant term
        const hasAnyVariantAlt = sourceMedias.some(media => {
          const alt = (media.dataset.mediaAlt || '').trim().toLowerCase();
          if (!alt) return false;
          return selectedTerms.some(t => matchesTerm(alt, t)) || otherTerms.some(t => matchesTerm(alt, t));
        });

        if (!hasAnyVariantAlt) {
          return false;
        }

        const isMediaMatch = (media) => {
          const alt = (media.dataset.mediaAlt || '').trim().toLowerCase();
          const mediaId = String(media.dataset.mediaId);

          if (variant.featured_media && mediaId === String(variant.featured_media.id)) {
            return true;
          }

          if (matchesTerm(alt, '#all') || matchesTerm(alt, 'all') || matchesTerm(alt, 'common')) {
            return true;
          }

          const matchesSelected = selectedTerms.some(t => matchesTerm(alt, t));
          const matchesOther = otherTerms.some(t => matchesTerm(alt, t));

          if (matchesSelected) return true;
          if (matchesOther) return false;
          return true;
        };

        const matchedMedias = sourceMedias.filter(isMediaMatch);
        const finalMedias = matchedMedias.length > 0 ? matchedMedias : sourceMedias;

        const matchedMediaIds = new Set(finalMedias.map(m => String(m.dataset.mediaId)));
        const finalThumbs = sourceThumbs.filter(t => matchedMediaIds.has(String(t.dataset.mediaId)));

        const galleryList = this.querySelector('.m-media-gallery__list');
        const thumbWrapper = this.querySelector('.m-product-media--slider__thumbnails .swiper-wrapper');

        if (galleryList) {
          const currentMedias = Array.from(galleryList.children);
          const isSameMedia = currentMedias.length === finalMedias.length && currentMedias.every((el, idx) => el === finalMedias[idx]);

          if (!isSameMedia) {
            const finalMediasSet = new Set(finalMedias);
            currentMedias.forEach(child => {
              if (!finalMediasSet.has(child)) {
                galleryList.removeChild(child);
              }
            });

            const layout = this.dataset.layout;
            finalMedias.forEach((media, index) => {
              media.dataset.index = index;
              media.style.display = '';

              if (layout === "layout-2") {
                media.classList.remove("m-col-span-2");
                if (index % 3 === 0) media.classList.add("m-col-span-2");
              } else if (layout === "layout-8") {
                media.classList.remove("m-col-span-2");
                if (index === 2 || index === 3) media.classList.add("m-col-span-2");
              } else if (layout === "layout-9") {
                media.classList.remove("m-col-span-2");
                if (index % 3 === 2 || finalMedias.length === 1) media.classList.add("m-col-span-2");
              }

              if (galleryList.children[index] !== media) {
                galleryList.insertBefore(media, galleryList.children[index] || null);
              }
            });
          }
        }

        if (thumbWrapper && finalThumbs.length > 0) {
          const currentThumbs = Array.from(thumbWrapper.children);
          const isSameThumb = currentThumbs.length === finalThumbs.length && currentThumbs.every((el, idx) => el === finalThumbs[idx]);

          if (!isSameThumb) {
            const finalThumbsSet = new Set(finalThumbs);
            currentThumbs.forEach(child => {
              if (!finalThumbsSet.has(child)) {
                thumbWrapper.removeChild(child);
              }
            });

            finalThumbs.forEach((thumb, index) => {
              thumb.dataset.index = index;
              thumb.style.display = '';
              if (thumbWrapper.children[index] !== thumb) {
                thumbWrapper.insertBefore(thumb, thumbWrapper.children[index] || null);
              }
            });
          }
        }

        if (!isInitial) {
          if (this.slider && typeof this.slider.update === 'function') {
            this.slider.update();
            if (this.slider.pagination && typeof this.slider.pagination.render === 'function') {
              this.slider.pagination.render();
            }
            if (this.slider.pagination && typeof this.slider.pagination.update === 'function') {
              this.slider.pagination.update();
            }
          }
          if (this.navSlider && typeof this.navSlider.update === 'function') {
            this.navSlider.update();
          }

          if (this.enableImageZoom) {
            this.handlePhotoswipe();
          }
        }

        return true;
      }

      setActiveMedia(variant) {
        if (!variant) return;

        this.filterMediaByAlt(variant);

        const currentListItems = Array.from(this.querySelectorAll('.m-media-gallery__list > .m-product-media--item'));

        let targetIndex = 0;
        if (variant.featured_media) {
          const idx = currentListItems.findIndex(item => String(item.dataset.mediaId) === String(variant.featured_media.id));
          if (idx >= 0) {
            targetIndex = idx;
          }
        }

        // Highlight thumbnail
        const currentThumbItems = Array.from(this.querySelectorAll('.m-product-media--slider__thumbnails .swiper-wrapper > .swiper-slide'));
        currentThumbItems.forEach((t, idx) => {
          t.classList.toggle('swiper-slide-thumb-active', idx === targetIndex);
        });

        // Pause other active media before playing new media
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia(this);
        }

        if (this.slider && this.slider.wrapperEl) {
          if (typeof this.slider.slideTo === 'function') {
            this.slider.slideTo(targetIndex, 300);
          }
          if (this.navSlider && typeof this.navSlider.slideTo === 'function') {
            this.navSlider.slideTo(targetIndex, 300);
          }

          // Trigger video autoplay for the target slide immediately
          const targetSlide = currentListItems[targetIndex];
          if (targetSlide) {
            this.playActiveMedia(targetSlide);
          }
        } else {
          if (currentListItems[targetIndex]) {
            this.scrollIntoView(currentListItems[targetIndex]);
          }
          // In grid layout (desktop non-slider), autoplay visible videos
          this.handleAutoplayVideo();
        }
      }

      scrollIntoView(selectedMedia) {
        selectedMedia.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      }
    }
  );

  // Global document-level hover zoom handler for all product media images
  if (!window._productMediaHoverZoomInitialized) {
    window._productMediaHoverZoomInitialized = true;
    let globalRafId = null;
    const GLOBAL_ACTIVE_TRANSITION = 'transform 0.45s cubic-bezier(0.25, 1, 0.5, 1), transform-origin 0.1s ease-out';
    const GLOBAL_OUT_TRANSITION = 'transform 0.45s cubic-bezier(0.25, 1, 0.5, 1)';

    document.addEventListener('mousemove', (e) => {
      if (window.innerWidth < 768) return;
      if (e.target.closest('.m-product-media--zoom-icon, .swiper-button-control, button, a')) return;

      const container = e.target.closest('.m-product-media, responsive-image.m-image');
      if (!container) return;

      const item = container.closest('.m-product-media--item, .m-main-product--media, .m-media-gallery');
      if (!item) return;
      if (item.classList.contains('media-type-video') || item.classList.contains('media-type-model') || item.classList.contains('media-type-external_video')) return;

      const img = container.querySelector('img') || item.querySelector('.m-image img, img');
      if (!img) return;

      if (globalRafId) cancelAnimationFrame(globalRafId);
      globalRafId = requestAnimationFrame(() => {
        const rect = container.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;

        const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
        const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));

        container.style.setProperty('overflow', 'hidden', 'important');
        img.style.setProperty('opacity', '1', 'important');
        img.style.setProperty('transition', GLOBAL_ACTIVE_TRANSITION, 'important');
        img.style.setProperty('transform-origin', `${x.toFixed(1)}% ${y.toFixed(1)}%`, 'important');
        img.style.setProperty('transform', 'scale(1.55)', 'important');
      });
    }, { passive: true });

    document.addEventListener('mouseout', (e) => {
      if (window.innerWidth < 768) return;
      const container = e.target.closest('.m-product-media, responsive-image.m-image');
      if (!container) return;

      const related = e.relatedTarget;
      if (related && container.contains(related)) return;

      const item = container.closest('.m-product-media--item, .m-main-product--media, .m-media-gallery');
      const img = container.querySelector('img') || (item && item.querySelector('.m-image img, img'));
      if (img) {
        img.style.setProperty('opacity', '1', 'important');
        img.style.setProperty('transition', GLOBAL_OUT_TRANSITION, 'important');
        img.style.setProperty('transform', 'scale(1)', 'important');
      }
    }, { passive: true });
  }
}
